/**
 * ProjectPicker — small modal to search & select a project from the Doserne
 * catalog. Used by the Daily Plan editor to insert a project "tab" into the
 * plan body so workers can jump straight to the project detail page.
 *
 * Returns { id, number, name, address } via `onPick`.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Search, MapPin, Hash, HardHat, User as UserIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectPicker({ open, onClose, onPick, authHeader }) {
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setTimeout(() => inputRef.current?.focus(), 50);
    if (projects !== null) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/projects?status=active`, authHeader());
        setProjects(data.projects || []);
      } catch {
        setProjects([]);
        toast.error("Kunne ikke laste prosjekter");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const list = projects || [];
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((p) => {
      const hay = [p.number, p.name, p.address, p.city].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [projects, q]);

  if (!open) return null;

  const pick = (p) => {
    onPick({ id: p.id, number: p.number || "", name: p.name || "", address: p.address || "" });
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center p-4 pt-24 sm:pt-32"
      onClick={onClose}
      data-testid="project-picker-backdrop"
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="project-picker-modal"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <HardHat className="w-5 h-5 text-sky-600" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Adaugă proiect</div>
            <div className="text-base font-black text-slate-900">Caută din catalog</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="project-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nume, adresă, număr..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              data-testid="project-picker-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {projects === null ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500 italic">
              {projects.length === 0 ? "Niciun proiect activ." : "Niciun rezultat pentru căutarea ta."}
            </div>
          ) : (
            <ul className="space-y-0.5" data-testid="project-picker-results">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => pick(p)}
                    className="w-full flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-sky-50 active:bg-sky-100 text-left"
                    data-testid={`project-pick-${p.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
                      <HardHat className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-black text-slate-900">
                        {p.number && <span className="inline-flex items-center gap-0.5 text-[10px] font-mono font-bold text-sky-700 bg-sky-100 rounded px-1 py-0.5"><Hash className="w-2.5 h-2.5" />{p.number}</span>}
                        <span className="truncate">{p.address || p.name || "(uten adresse)"}</span>
                      </div>
                      {p.name && p.name !== p.address && (
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                          <UserIcon className="w-3 h-3 flex-shrink-0" /> {p.name}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
